import UniversalWebView from '@/components/UniversalWebView'
import { UniversalWebViewRef } from '@/components/universalWebView/types'
import { getAuthToken } from '@/lib/api/authToken'
import { usePostKeyboardEventsToWebView } from '@/lib/hooks/usePostKeyboardEventsToWebView'
import { useSwitchTeam } from '@/lib/hooks/useSwitchTeam'
import { handleClickToPay } from '@/modules/handleClickToPay'
import { useAppSelector } from '@/redux/store'
import { PlanCode } from '@/typings/domain'
import { getCameraPermissionsAsync } from 'expo-image-picker'
import { router } from 'expo-router'
import { get } from 'lodash-es'
import { useCallback, useMemo, useRef } from 'react'

export default function TableData({
  tableToken,
  searchParams,
}: {
  tableToken: string
  searchParams?: Record<string, string>
}) {
  const switchTeam = useSwitchTeam()
  const currentUser = useAppSelector(state => state.currentUser.user)
  const webviewRef = useRef<UniversalWebViewRef>(null)
  usePostKeyboardEventsToWebView(webviewRef)

  const uri = useMemo(() => {
    const token = getAuthToken()
    const base = `${process.env.EXPO_PUBLIC_NEXT_BASE_URL}/m/tables/${tableToken}?m_t=${token}`
    if (!searchParams || Object.keys(searchParams).length === 0) return base
    const extra = new URLSearchParams(searchParams).toString()
    return `${base}&${extra}`
  }, [tableToken, searchParams])

  const handleMessage = useCallback(
    async (data: { event: string; details: unknown }) => {
      if (data.event === 'CLICK_TO_PAY') {
        const recommendedPlanCode = get(data.details, 'recommendedPlanCode') as PlanCode | undefined
        handleClickToPay({ recommendedPlanCode, currentUser: currentUser ?? undefined })
        return
      }

      if (data.event === 'FORM_NOT_FOUND') {
        router.replace('/tables')
        return
      }

      if (data.event === 'SWITCH_TEAM') {
        const userId = get(data.details, 'userId') as string | undefined
        if (userId) {
          switchTeam(userId)
        } else {
          router.push('/profile/switchUser')
        }
        return
      }

      if (data.event === 'REQUEST_CAMERA_PERMISSION') {
        const { granted } = await getCameraPermissionsAsync()
        webviewRef.current?.postMessage(JSON.stringify({ event: 'CAMERA_PERMISSION_RESULT', details: { granted } }))
        return
      }
    },
    [currentUser, switchTeam]
  )

  return (
    <UniversalWebView
      ref={webviewRef}
      source={{ uri }}
      className="flex-1 bg-background-muted"
      allow="geolocation;camera;microphone"
      onMessage={handleMessage}
      onContentReady={() => {}}
      scrollEnabled={false}
    />
  )
}
